import * as React from "react";
import { useLocation } from "react-router";

import { claimAgentDraftFor, type AgentDraft } from "@/shared/domain/agents";

import {
  draftToFormState,
  emptyFormState,
  type FormState,
} from "./buildRequestTemplate";

/**
 * Claim any pending `client_create_agent` draft for this editor route and
 * return a `useState`-shaped `[form, setForm]` tuple plus the update draft
 * (if any) the AgentEditor should overlay onto the fetched agent.
 *
 * Which drafts belong to which route is owned by
 * {@link claimAgentDraftFor}; this hook only decides what to do with a
 * claimed one. On the create route it seeds the form directly. On an edit
 * route the form starts empty and the claimed draft is handed back for the
 * editor to overlay once the saved agent lands — the editor is the only
 * layer that has the saved values to merge into.
 *
 * The claim is re-run on every navigation, not just on mount and not just when
 * the routed id changes. React Router reuses one AgentEditor element across
 * `/agents/:agentId` navigations, so a mount-only claim would miss the handoff
 * whenever the user already had an agent editor open — which is the common
 * case, since the AI runs in a side panel over whatever page the user is on.
 * Keying on the routed id alone still missed the sharpest version of that:
 * updating the agent the user is *already looking at* pushes the unchanged
 * URL, so nothing about the route identity moves and the draft would sit in
 * sessionStorage forever. `location.key` is the signal that does move — React
 * Router mints a fresh one even for a same-URL push.
 *
 * An unapplied claim survives a re-key on the same route. Navigations happen
 * for reasons that have nothing to do with the handoff (a tab change, a query
 * param), and re-claiming returns null once the draft has been consumed from
 * storage; overwriting the held claim with that null would strand a proposal
 * the editor had not yet overlaid.
 *
 * Claiming during render (rather than in an effect) is deliberate: the
 * AgentEditor's seed effect reads the returned draft in the same pass that
 * observes the newly-loaded agent. Deferring the claim to an effect would let
 * that seed run first with the previous route's draft and mark the new agent
 * as already seeded, so the fresh draft would never be applied.
 *
 * Idempotency under React 18 StrictMode is owned by the draft-consume cache
 * in `agentDraft.ts`, not by this hook: StrictMode double-invokes render-phase
 * functions in development, so the claim runs twice back-to-back, and the
 * cache makes both runs see the same draft. See `agentDraft.ts` for the cache
 * lifecycle.
 */
export function useDraftSeededForm(
  mode: "create" | "edit",
  agentId?: string,
): [
  FormState,
  React.Dispatch<React.SetStateAction<FormState>>,
  AgentDraft | null,
] {
  const route = `${mode}:${agentId ?? ""}`;
  const { key: locationKey } = useLocation();
  // Keyed by route *and* navigation so both a param change and a same-URL
  // push re-claim. Holds the claim itself rather than a "have we claimed"
  // flag, so the value survives the re-renders between the claim and the
  // editor consuming it.
  const claimRef = React.useRef<{
    route: string;
    locationKey: string;
    draft: AgentDraft | null;
  } | null>(null);
  const previous = claimRef.current;
  const current =
    previous !== null &&
    previous.route === route &&
    previous.locationKey === locationKey
      ? previous
      : {
          route,
          locationKey,
          // Leaving the route abandons whatever it had pending; staying on it
          // keeps an unapplied claim alive across an unrelated navigation.
          draft:
            claimAgentDraftFor({ mode, agentId }) ??
            (previous !== null && previous.route === route
              ? previous.draft
              : null),
        };
  claimRef.current = current;
  const claimed = current.draft;

  const [form, setForm] = React.useState<FormState>(() =>
    mode === "create" && claimed !== null
      ? draftToFormState(claimed)
      : emptyFormState(),
  );
  return [form, setForm, mode === "edit" ? claimed : null];
}
